/*
 * Motion tokens + standard choreography (DESIGN.md §7).
 *
 * The single source of truth for how the Hub moves. Components import the
 * spring presets and variants from here so timing stays coherent; when
 * DESIGN.md §7 changes, change it here in the same PR.
 *
 * Hard rule: every animated component branches on Framer Motion's
 * useReducedMotion(). When the user opts out, transitions snap to their end
 * state and ambient motion (shimmer, glow pulse) is disabled. The helpers
 * here make that branch a one-liner (see `enter`).
 */
import type { Transition, Variants } from 'framer-motion';

/** Durations in seconds (Framer Motion's unit), mirroring the CSS ms tokens. */
export const DURATION = {
  fast: 0.12, // button taps, chip presses
  med: 0.22, // page transitions, modal in/out, sheet slides
  slow: 0.38, // hero reveals, celebration flashes
} as const;

/** Cubic-bezier easings, typed as Framer's 4-tuple. */
export const EASE = {
  out: [0.2, 0.8, 0.2, 1] as [number, number, number, number], // default
  in: [0.4, 0, 1, 1] as [number, number, number, number], // leaving the screen
  bounce: [0.34, 1.56, 0.64, 1] as [number, number, number, number], // success
} as const;

/** Spring presets. */
export const SPRING_SOFT: Transition = {
  type: 'spring',
  stiffness: 260,
  damping: 22,
}; // cards entering
export const SPRING_SNAPPY: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 28,
}; // taps, toggles
export const SPRING_LOOSE: Transition = {
  type: 'spring',
  stiffness: 180,
  damping: 16,
}; // playful bounces

/* ---- Standard choreography (DESIGN.md §7) ---- */

/** Page transition: fade + 12px slide up, 220ms, ease-out. */
export const pageVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.med, ease: EASE.out },
  },
};

/** Card / hero enter: fade + slide up from below, spring-soft. */
export const cardVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: SPRING_SOFT },
};

/** List item enter (use on a child with a staggered parent). */
export const listItemVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: SPRING_SOFT },
};

/** Parent that staggers its children in (50ms apart, DESIGN.md §7). */
export const staggerParent: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};

/**
 * Resolve the initial/animate pair for an enter animation, honoring a
 * reduced-motion preference. Pass the result straight into a motion element:
 *
 *   const reduce = useReducedMotion();
 *   <motion.div {...enter(cardVariants, reduce)} />
 *
 * When reduced motion is on, `initial` is `false` so the element mounts in
 * its final state with no transition.
 */
export function enter(variants: Variants, reduce: boolean | null) {
  if (reduce) return { initial: false as const, animate: 'show' as const, variants };
  return { initial: 'hidden' as const, animate: 'show' as const, variants };
}

/** Button tap: scale to 0.97, spring-snappy. Undefined when reduced. */
export function tapScale(reduce: boolean | null) {
  return reduce ? undefined : { scale: 0.97 };
}
